import { HttpClient } from '../api/client'
import { deduplicateRequest } from '../api/deduplicateRequest'
import { TokenManager } from '../auth/tokenManager'
import type { ApiResponse, MindMapEndpoints, MindMapNode, MindMapEdge } from '@bandos/shared'

const dedupedList = deduplicateRequest(
  async () => {
    const headers = await TokenManager.getAuthHeaders()
    return HttpClient.get<MindMapEndpoints['list']['response']>('/mindmaps', headers)
  },
  { keyFn: () => 'list' }
)

const dedupedGet = deduplicateRequest(async (id: string) => {
  const headers = await TokenManager.getAuthHeaders()
  return HttpClient.get<MindMapEndpoints['get']['response']>(`/mindmaps/${id}`, headers)
})

export class MindmapService {
  /**
   * Get all mindmaps for the current user
   */
  static list(): Promise<ApiResponse<MindMapEndpoints['list']['response']>> {
    return dedupedList()
  }

  /**
   * Get a specific mindmap by ID
   */
  static get(id: string): Promise<ApiResponse<MindMapEndpoints['get']['response']>> {
    return dedupedGet(id)
  }

  /**
   * Get a public mindmap by ID (no authentication required)
   * Only works for mindmaps where is_public = true
   */
  static async getPublic(id: string): Promise<ApiResponse<MindMapEndpoints['get']['response']>> {
    // No auth headers - this is a public endpoint
    return HttpClient.get<MindMapEndpoints['get']['response']>(`/public/mindmaps/${id}`)
  }

  /**
   * Create a new mindmap
   */
  static async create(
    mindmapData: MindMapEndpoints['create']['body']
  ): Promise<ApiResponse<MindMapEndpoints['create']['response']>> {
    const headers = await TokenManager.getAuthHeaders()
    const result = await HttpClient.post<MindMapEndpoints['create']['response']>(
      '/mindmaps',
      mindmapData,
      headers
    )

    return result
  }

  /**
   * Update an existing mindmap
   */
  static async update(
    id: string,
    updates: MindMapEndpoints['update']['body']
  ): Promise<ApiResponse<MindMapEndpoints['update']['response']>> {
    const headers = await TokenManager.getAuthHeaders()
    return HttpClient.put<MindMapEndpoints['update']['response']>(
      `/mindmaps/${id}`,
      updates,
      headers
    )
  }

  /**
   * Delete a mindmap
   */
  static async delete(id: string): Promise<ApiResponse<MindMapEndpoints['delete']['response']>> {
    const headers = await TokenManager.getAuthHeaders()
    return HttpClient.delete<MindMapEndpoints['delete']['response']>(`/mindmaps/${id}`, headers)
  }

  /**
   * Duplicate an existing mindmap (deep copy with labels and stakeholders)
   */
  static async duplicate(
    id: string
  ): Promise<ApiResponse<MindMapEndpoints['duplicate']['response']>> {
    const headers = await TokenManager.getAuthHeaders()
    return HttpClient.post<MindMapEndpoints['duplicate']['response']>(
      `/mindmaps/${id}/duplicate`,
      {},
      headers
    )
  }

  /**
   * Save mindmap data (nodes and edges) - convenience method for persistence
   */
  static async save(
    id: string,
    data: { nodes: MindMapNode[]; edges: MindMapEdge[]; title?: string; force?: boolean }
  ): Promise<ApiResponse<MindMapEndpoints['update']['response']>> {
    const updateData: MindMapEndpoints['update']['body'] = {
      content: { nodes: data.nodes, edges: data.edges },
    }

    if (data.title) {
      updateData.title = data.title
    }

    if (data.force) {
      updateData.force = true
    }

    return this.update(id, updateData)
  }

  /**
   * Get integration configuration for a specific mindmap and platform
   */
  static async getIntegrationConfig(
    mindmapId: string,
    platform: string
  ): Promise<ApiResponse<{ platform: string; config: Record<string, unknown> | null }>> {
    const headers = await TokenManager.getAuthHeaders()
    return HttpClient.get<{ platform: string; config: Record<string, unknown> | null }>(
      `/mindmaps/${mindmapId}/integration-config/${platform}`,
      headers
    )
  }

  /**
   * Update integration configuration for a specific mindmap and platform
   */
  static async updateIntegrationConfig(
    mindmapId: string,
    platform: string,
    config: { boardId?: string; listId?: string; projectKey?: string; [key: string]: unknown }
  ): Promise<ApiResponse<{ platform: string; config: Record<string, unknown> }>> {
    const headers = await TokenManager.getAuthHeaders()
    return HttpClient.put<{ platform: string; config: Record<string, unknown> }>(
      `/mindmaps/${mindmapId}/integration-config/${platform}`,
      config,
      headers
    )
  }
}
